/**
 * SettingsView — the campaign's send configuration: which sequence to use, who
 * it appears to come from, when it may go out, and the follow-up limits.
 *
 * Pause is NOT a field. The BE exposes it as two verbs
 * (`POST …/settings/pause` / `…/resume`) and drops any `paused_at` a PUT
 * carries, so the switch lives in its own section behind its own confirmation
 * and never rides along with Save.
 *
 * The form is seeded from the query and re-seeded whenever the server answer
 * changes — but only while the user has not edited it, so a background refetch
 * (or the SSE bridge) can't wipe a half-typed value.
 *
 * `send_tz` is canonicalised on the way in: the Schedule picker only lists modern
 * IANA names, while the BE's `time.LoadLocation` accepts the tzdata "backward"
 * links too, so a row written by anything other than this form (a seeded
 * campaign, a curl/Postman PUT) could hold `Asia/Calcutta` and show a raw alias.
 * The zone is read-only here (Z1): the Schedule picker is where it changes.
 *
 * Follow-ups are always agent-drafted since 2.7 (Ruling 13 retired
 * `ai_followups`; the BE still accepts and ignores it on PUT).
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useOutreachSettings, useOutreachSettingsMutations, useOutreachTemplates } from '@discovr/features';
import { DEFAULT_OUTREACH_REGION, type OutreachSettings, type OutreachSettingsInput } from '@discovr/types';
import { countryName, countryOptions } from '../../../lib/countries';
import { canonicalId, zoneLabel } from '../../../lib/timeZones';
import { cn } from '../../../lib/utils';
import { fmtDateTime } from '../../utils/campaignDetailHelpers';
import { ActionConfirmDialog } from '../../../components/common/ActionConfirmDialog';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Skeleton } from '../../../components/ui/skeleton';
import { Switch } from '../../../components/ui/switch';
import { RosterScreenHeader } from '../parts/RosterScreenHeader';
import { SettingsSection } from './SettingsSection';
import { chosenTemplateName, TEMPLATE_SETTINGS_COPY } from './defaultSequenceTemplate';
import { apiErrorCopy, type ErrorContext } from './sendLabels';

export interface SettingsViewProps {
  campaignId: string;
  canWrite: boolean;
}

type ErrorField = 'zone' | 'region' | 'template' | 'followupCap' | 'ceiling';

/** The BE's per-field validation codes (internal/outreach/settings/validate.go)
 *  plus the template resolution failures its Put can surface. Anything else is
 *  a form-level failure. */
const FIELD_BY_CODE: Record<string, ErrorField> = {
  INVALID_SEND_TZ: 'zone',
  INVALID_REGION: 'region',
  INVALID_FOLLOWUP_CAP: 'followupCap',
  INVALID_WALK_AWAY_CEILING: 'ceiling',
};

/** The BE's messages are snake_case field names; the admin reads the field's label. */
const FIELD_COPY: Record<string, string> = {
  INVALID_SEND_TZ: 'The campaign time zone is not valid. Choose another one in the Schedule picker.',
  INVALID_REGION: 'That region is not a valid country. Choose one from the list.',
  INVALID_FOLLOWUP_CAP: 'Follow-up daily cap must be between 1 and 5,000.',
  INVALID_WALK_AWAY_CEILING: 'Walk-away ceiling must be more than 0 — leave it empty for no ceiling.',
};

export const SETTINGS_CHANGED_COPY = 'These settings changed in another session — reload to see them'; // copy: ours

function isSettingsChanged(err: unknown): boolean {
  if (errorCode(err) === 'SETTINGS_CHANGED') return true;
  return typeof err === 'object' && err !== null && (err as { httpStatus?: unknown }).httpStatus === 412;
}

function saveErrorCopy(code: string | undefined, err: unknown): string {
  const fieldCopy = code && Object.hasOwn(FIELD_COPY, code) ? FIELD_COPY[code] : undefined;
  if (fieldCopy) return fieldCopy;
  return apiErrorCopy(err, 'Could not save these settings.', code?.startsWith('TEMPLATE_') ? 'template' : 'save');
}

interface SettingsForm {
  sequenceTemplateId: number | null;
  askRateFirst: boolean;
  autoFollowups: boolean;
  sendTz: string;
  /** "" is the India default; otherwise the saved country code. */
  region: string;
  /** Kept as text so an emptied field stays empty instead of collapsing to 0. */
  walkAwayCeiling: string;
  maxRounds: string;
  followupDailyCap: string;
}

function toForm(s: OutreachSettings): SettingsForm {
  return {
    sequenceTemplateId: s.sequenceTemplateId ?? null,
    askRateFirst: s.askRateFirst,
    autoFollowups: s.autoFollowups ?? true,
    sendTz: canonicalId(s.sendTz),
    region: s.regionSet && s.region !== DEFAULT_OUTREACH_REGION ? s.region : '',
    walkAwayCeiling: s.walkAwayCeiling == null ? '' : String(s.walkAwayCeiling),
    maxRounds: String(s.maxRounds),
    followupDailyCap: String(s.followupDailyCap),
  };
}

function toInput(f: SettingsForm): OutreachSettingsInput {
  const ceiling = f.walkAwayCeiling.trim();
  return {
    sequenceTemplateId: f.sequenceTemplateId ?? undefined,
    askRateFirst: f.askRateFirst,
    autoFollowups: f.autoFollowups,
    sendTz: f.sendTz,
    walkAwayCeiling: ceiling === '' ? undefined : Number(ceiling),
    maxRounds: Number(f.maxRounds),
    followupDailyCap: Number(f.followupDailyCap),
  };
}

/** Compared through `toInput` so both sides share one key order, and so a
 *  purely cosmetic edit (trailing space in a number) isn't "dirty". */
function sameForm(a: SettingsForm, b: SettingsForm): boolean {
  return a.region === b.region && JSON.stringify(toInput(a)) === JSON.stringify(toInput(b));
}

function errorCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function errorMessage(err: unknown, fallback: string, context: ErrorContext = 'save'): string {
  return apiErrorCopy(err, fallback, context);
}

export function SettingsView({ campaignId, canWrite }: SettingsViewProps) {
  const uid = useId();
  const settingsQuery = useOutreachSettings(campaignId);
  const templatesQuery = useOutreachTemplates(campaignId);
  const { save, pause, resume } = useOutreachSettingsMutations(campaignId);

  const settings = settingsQuery.data;
  const baseline = useMemo(() => (settings ? toForm(settings) : null), [settings]);

  const [form, setForm] = useState<SettingsForm | null>(baseline);
  // Sentinel for "the server answer changed", so a refetch can re-seed a
  // pristine form without stamping over one the user is editing.
  const [seededFrom, setSeededFrom] = useState<SettingsForm | null>(baseline);
  // The updatedAt of the answer the form was seeded from — the save's precondition.
  const [loadedAt, setLoadedAt] = useState<string | undefined>(settings?.updatedAt);
  // An untouched region follows the server on a re-seed under a dirty form, so it never becomes a write.
  const [regionTouched, setRegionTouched] = useState(false);
  if (baseline !== seededFrom) {
    const wasDirty = !!form && !!seededFrom && !sameForm(form, seededFrom);
    setSeededFrom(baseline);
    // A save's own answer matches the form, so it re-seeds even though the form differs from the old seed.
    if (!wasDirty || !form || (baseline && sameForm(form, baseline))) {
      setForm(baseline);
      setLoadedAt(settings?.updatedAt);
      setRegionTouched(false);
    } else if (baseline && !regionTouched && form.region !== baseline.region) {
      setForm({ ...form, region: baseline.region });
    }
  }

  const [fieldError, setFieldError] = useState<{ field: ErrorField; message: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'pause' | 'resume' | null>(null);
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);
  // Save lives in the header and Limits at the foot of the page: a refusal
  // must bring its field into view or it reads as a no-op (E2E J6).
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!fieldError) return;
    const invalid = rootRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], [data-field-error="true"]');
    invalid?.scrollIntoView?.({ block: 'center' });
    invalid?.focus();
  }, [fieldError]);

  const errorFor = (field: ErrorField) => (fieldError?.field === field ? fieldError.message : undefined);

  if (settingsQuery.isError) {
    return <p className="py-16 text-center text-sm text-rose-500">Couldn&apos;t load outreach settings. Please refresh.</p>;
  }
  if (!form || !baseline) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-10 w-64 rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  // Editing anything retires the last server verdict: leaving an error
  // under a field the user has since corrected would just be wrong.
  const patch = (next: Partial<SettingsForm>) => {
    setFieldError(null);
    setFormError(null);
    if ('region' in next) setRegionTouched(true);
    setForm((prev) => (prev ? { ...prev, ...next } : prev));
  };

  const dirty = !sameForm(form, baseline);
  const paused = !!settings?.pausedAt;
  const busy = save.isPending || pause.isPending || resume.isPending;

  const handleSave = async () => {
    setFieldError(null);
    setFormError(null);
    try {
      await save.mutateAsync({
        ...toInput(form),
        ...(form.region !== baseline.region ? { region: form.region } : {}),
        ...(loadedAt ? { ifUnmodifiedSince: loadedAt } : {}),
      });
      toast.success('Settings saved');
    } catch (err) {
      if (isSettingsChanged(err)) {
        setConflict(true);
        toast.error('Settings not saved', { description: SETTINGS_CHANGED_COPY });
        return;
      }
      const code = errorCode(err);
      const message = saveErrorCopy(code, err);
      const field = code?.startsWith('TEMPLATE_') ? 'template' : code ? FIELD_BY_CODE[code] : undefined;
      if (field) setFieldError({ field, message });
      else setFormError(message);
      toast.error('Settings not saved', { description: message });
    }
  };

  const handleReload = async () => {
    setReloading(true);
    try {
      const { data } = await settingsQuery.refetch();
      if (!data) return;
      setForm(toForm(data));
      setLoadedAt(data.updatedAt);
      setRegionTouched(false);
      setConflict(false);
      setFieldError(null);
      setFormError(null);
    } finally {
      setReloading(false);
    }
  };

  // Kept open for the round trip so the dialog can show its own busy state;
  // closed either way once it settles.
  const runPauseToggle = async () => {
    const action = confirming;
    if (!action) return;
    setFormError(null);
    try {
      await (action === 'pause' ? pause.mutateAsync() : resume.mutateAsync());
    } catch (err) {
      setFormError(errorMessage(err, `Could not ${action} outreach.`));
    } finally {
      setConfirming(null);
    }
  };

  const regionError = errorFor('region');
  const templateError = errorFor('template');
  const pauseDescription = paused
    ? `Paused since ${fmtDateTime(settings?.pausedAt)} — queued sends stay put until you resume.`
    : 'Pausing holds every queued and scheduled send until you turn it back on.';

  return (
    <div ref={rootRef} className="flex min-h-0 flex-1 flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <RosterScreenHeader
          title="Settings"
          subtitle="Merge tags apply per creator at send time."
        />
        {canWrite ? (
          <Button type="button" loading={save.isPending} disabled={!dirty || busy || conflict} onClick={() => void handleSave()}>
            Save
          </Button>
        ) : null}
      </div>

      {conflict ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-caption-regular text-amber-800">
          <span>{SETTINGS_CHANGED_COPY}</span>
          <Button type="button" size="sm" variant="outline" loading={reloading} disabled={reloading} onClick={() => void handleReload()}>
            Reload
          </Button>
        </div>
      ) : null}

      {formError ? (
        <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-caption-regular text-rose-600">
          {formError}
        </p>
      ) : null}

      <SettingsSection title="Time zone" description="Every scheduled send uses this zone.">
        <div role="group" aria-labelledby={`${uid}-zone-label`} className="flex flex-col gap-1.5">
          <span id={`${uid}-zone-label`} className="text-body-sm-medium text-foreground">Campaign time zone</span>
          <p
            tabIndex={errorFor('zone') ? -1 : undefined}
            data-field-error={errorFor('zone') ? 'true' : undefined}
            aria-describedby={errorFor('zone') ? `${uid}-zone-error` : undefined}
            className="text-body-sm-regular text-foreground"
          >
            {zoneLabel(form.sendTz)}
          </p>
          <p className="text-caption-regular text-muted-foreground">
            {canWrite ? 'It is changed from the Schedule picker when you send. ' : ''}Send now uses your own time zone.
          </p>
          {errorFor('zone') ? (
            <p id={`${uid}-zone-error`} role="alert" className="text-caption-regular text-rose-600">
              {errorFor('zone')}
            </p>
          ) : null}
        </div>
      </SettingsSection>

      <SettingsSection title="Region">
        <Field label="Outreach region" id={`${uid}-region`} error={regionError}>
          <RegionSelect
            id={`${uid}-region`}
            value={form.region}
            disabled={!canWrite || busy}
            invalid={!!regionError}
            describedBy={regionError ? `${uid}-region-help ${uid}-region-error` : `${uid}-region-help`}
            onChange={(region) => patch({ region })}
          />
          <p id={`${uid}-region-help`} className="text-caption-regular text-muted-foreground">
            US adds an unsubscribe link and our postal address to every mail. Other regions send plain personal mail.
          </p>
        </Field>
      </SettingsSection>

      <SettingsSection
        title="Sequence"
        description="Step 1 is the first touch; later steps are the follow-ups drafted for approval."
      >
        <div className="flex flex-col gap-1.5 md:col-span-3">
          <p className="text-body-sm-medium text-foreground">Sequence template</p>
          <p className="text-body-sm-regular text-foreground">{chosenTemplateName(templatesQuery.data, form.sequenceTemplateId)}</p>
          {templateError ? <p className="text-caption-regular text-rose-600">{templateError}</p> : null}
          <p className="text-caption-regular text-muted-foreground">{TEMPLATE_SETTINGS_COPY}</p>
          <p className="text-caption-regular text-muted-foreground">
            {"The agent writes each creator's email from this template and the campaign brief. Kept as written: the ask, figures, links. Reworded: the body. Subject: tidied once by AI for the whole campaign; tags like {creator_name} fill in per creator."}
          </p>
        </div>
      </SettingsSection>

      <SettingsSection title="Options">
        <Toggle
          id={`${uid}-ask-rate-first`}
          label="Ask rate first"
          help="Open by asking the creator for their rate before pitching the deliverables."
          checked={form.askRateFirst}
          disabled={!canWrite || busy}
          onChange={(v) => patch({ askRateFirst: v })}
        />
        <Toggle
          id={`${uid}-auto-followups`}
          label="Automatic follow-ups"
          help="Send the template's follow-up steps by themselves. Switched off, due steps wait and go out once it is back on."
          checked={form.autoFollowups}
          disabled={!canWrite || busy}
          onChange={(v) => patch({ autoFollowups: v })}
        />
      </SettingsSection>

      <SettingsSection title="Limits">
        <NumberField
          id={`${uid}-cap`}
          label="Follow-up daily cap"
          value={form.followupDailyCap}
          min={1}
          max={5000}
          disabled={!canWrite || busy}
          error={errorFor('followupCap')}
          onChange={(v) => patch({ followupDailyCap: v })}
        />
        <NumberField
          id={`${uid}-ceiling`}
          label="Walk-away ceiling"
          value={form.walkAwayCeiling}
          min={0}
          // The ceiling is a price (numeric(12,2) on the BE), not a count —
          // the default step of 1 would mark 1500.50 invalid in the browser.
          step="any"
          disabled={!canWrite || busy}
          error={errorFor('ceiling')}
          help="Leave empty for no ceiling."
          onChange={(v) => patch({ walkAwayCeiling: v })}
        />
      </SettingsSection>

      <SettingsSection title="Pause" description={pauseDescription}>
        <div className="flex flex-wrap items-center gap-3 md:col-span-3">
          <Badge variant={paused ? 'secondary' : 'default'}>{paused ? 'Paused' : 'Active'}</Badge>
          {canWrite ? (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirming(paused ? 'resume' : 'pause')}
            >
              {paused ? 'Resume outreach' : 'Pause outreach'}
            </Button>
          ) : null}
        </div>
      </SettingsSection>

      <ActionConfirmDialog
        open={confirming === 'pause'}
        onOpenChange={(next) => {
          if (!next) setConfirming(null);
        }}
        variant="change"
        title="Pause outreach?"
        description="Queued sends will wait until you resume."
        confirmLabel="Pause"
        busy={pause.isPending}
        onConfirm={() => runPauseToggle()}
      />
      <ActionConfirmDialog
        open={confirming === 'resume'}
        onOpenChange={(next) => {
          if (!next) setConfirming(null);
        }}
        variant="reinstate"
        title="Resume outreach?"
        description="Waiting sends go out again."
        confirmLabel="Resume"
        busy={resume.isPending}
        onConfirm={() => runPauseToggle()}
      />
    </div>
  );
}

interface FieldProps {
  label: string;
  id: string;
  error?: string;
  className?: string;
  children: ReactNode;
}

function Field({ label, id, error, className, children }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="text-caption-regular text-rose-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}

interface RegionSelectProps {
  id: string;
  value: string;
  disabled: boolean;
  invalid: boolean;
  describedBy: string;
  onChange: (next: string) => void;
}

function RegionSelect({ id, value, disabled, invalid, describedBy, onChange }: RegionSelectProps) {
  const countries = useMemo(() => countryOptions().filter((c) => c.code !== DEFAULT_OUTREACH_REGION), []);
  const listed = value === '' || countries.some((c) => c.code === value);
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-lg border border-border bg-transparent px-3 text-body-sm-regular text-foreground disabled:opacity-60"
    >
      <option value="">{countryName(DEFAULT_OUTREACH_REGION)} (default)</option>
      {listed ? null : <option value={value}>{value}</option>}
      {countries.map((c) => (
        <option key={c.code} value={c.code}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

interface ToggleProps {
  id: string;
  label: string;
  help: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}

function Toggle({ id, label, help, checked, disabled, onChange }: ToggleProps) {
  return (
    <div className="flex items-start gap-3">
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} className="mt-0.5" />
      <div className="min-w-0">
        <Label htmlFor={id} className="text-body-sm-medium text-foreground">
          {label}
        </Label>
        <p className="text-caption-regular text-muted-foreground">{help}</p>
      </div>
    </div>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  value: string;
  min?: number;
  max?: number;
  step?: string;
  disabled: boolean;
  error?: string;
  help?: string;
  onChange: (next: string) => void;
}

function NumberField({ id, label, value, min, max, step, disabled, error, help, onChange }: NumberFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? (
        <p className="text-caption-regular text-rose-600">{error}</p>
      ) : help ? (
        <p className="text-caption-regular text-muted-foreground">{help}</p>
      ) : null}
    </div>
  );
}
